import type { Theme, ThemeTokens } from './types';

// Camel-case → kebab-case for CSS custom property names.
// `bodyBackground` → `--theme-body-background`.
function tokenToCssVar(name: string): string {
  return `--theme-${name.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}`;
}

// Write the theme's tokens onto <html> and tag it with `data-theme={key}`.
// Returns a function that resets every variable touched, so callers (e.g.
// the live-preview picker) can roll back without bleeding state into the
// next mount.
export function applyTheme(theme: Theme): () => void {
  const root = document.documentElement;
  const previousKey = root.getAttribute('data-theme');
  root.setAttribute('data-theme', theme.key);

  const previous: Record<string, string> = {};
  for (const [name, value] of Object.entries(theme.tokens) as [keyof ThemeTokens, string][]) {
    const cssVar = tokenToCssVar(name);
    previous[cssVar] = root.style.getPropertyValue(cssVar);
    root.style.setProperty(cssVar, value);
  }

  return () => {
    if (previousKey === null) root.removeAttribute('data-theme');
    else root.setAttribute('data-theme', previousKey);
    for (const [cssVar, prevValue] of Object.entries(previous)) {
      if (prevValue) root.style.setProperty(cssVar, prevValue);
      else root.style.removeProperty(cssVar);
    }
  };
}
